import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import type { BrokerLead, FollowUpRequest } from '../../api/types';
import { EmptyState } from '../../components/EmptyState';
import { ErrorMessage } from '../../components/ErrorMessage';
import { PageLoader } from '../../components/Loader';
import { MutationError } from '../../components/MutationError';
import { useLeadStatusLabels } from '../../ops/leadStatus';
import { useProject } from '../../projects/context';
import { projectKeys, useProjectApi } from '../../projects/useProjectApi';
import {
  type ContactFilter,
  canRequest,
  contactBadges,
  contactFilters,
  defaultTopic,
  fmtDay,
  fmtDayTime,
  matchesFilter,
  matchesSearch,
  needsFollowUp,
  type RequestPreset,
  type RequestTopic,
  requestPresets,
  requestTopics,
  sortForFollowUp,
} from './contactRules';
import { useBrokerLeads } from './useBrokerLeads';

// Makler-Sicht „Kontakte & Nachfassen“ (OFFENE_FRAGEN B-28, Dominik 12.10.2026): alle Leads, mit denen der Makler in Kontakt
// war oder ist, je Lead mit Bezug und Stand des Nachfassens. Nachfassen machen die Agenten im Support – der Makler beauftragt
// es hier (Anlass, Zeitpunkt, Hinweis), sieht, wer dran ist, und bekommt das Ergebnis als Benachrichtigung.
export function BrokerContacts() {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const query = useBrokerLeads();
  const labels = useLeadStatusLabels();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [now] = useState(() => new Date());
  const [formFor, setFormFor] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const request = useMutation({
    mutationFn: (body: FollowUpRequest) => papi.requestFollowUp(body),
    onSuccess: (f) => {
      setFormFor(null);
      setConfirmation(
        `Beauftragt: ${f.assigned_user_name ?? 'Die Terminplanung'} fasst bei ${f.lead_name} nach, fällig ${fmtDayTime(f.due_at)}. Das Ergebnis bekommen Sie als Benachrichtigung.`,
      );
      void queryClient.invalidateQueries({ queryKey: projectKeys.brokerLeads(project.id) });
    },
  });
  const filter = (contactFilters.find((f) => f.key === params.get('bezug'))?.key ?? 'alle') satisfies ContactFilter;
  const setFilter = (key: ContactFilter) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === 'alle') next.delete('bezug');
        else next.set('bezug', key);
        return next;
      },
      { replace: true },
    );
  const openForm = (id: string | null) => {
    request.reset();
    setConfirmation('');
    setFormFor(id);
  };

  if (query.isPending) return <PageLoader label="Kontakte werden geladen …" />;
  if (query.isError) return <ErrorMessage error={query.error} onRetry={() => void query.refetch()} />;
  const all = sortForFollowUp(query.data.items, now);
  const shown = all.filter((l) => matchesFilter(l, filter, now) && matchesSearch(l, search));

  return (
    <div className="embedded-page broker-contacts">
      <p className="muted">
        Alle Leads, mit denen Sie in Kontakt waren oder sind. Oben steht, wo nachzufassen ist. Das Nachfassen übernimmt der Support: Sie
        beauftragen es und sehen hier, wer dran ist und was herauskam.
      </p>
      <form className="filters" role="search" aria-label="Kontakte filtern" onSubmit={(e) => e.preventDefault()}>
        <div className="filters-row">
          <label className="field grow">
            <span>Suche</span>
            <input type="search" value={search} placeholder="Name, Ort, PLZ oder Telefon" onChange={(e) => setSearch(e.target.value)} />
          </label>
        </div>
        <div className="chips" role="group" aria-label="Auswahl">
          {contactFilters.map((f) => (
            <button key={f.key} type="button" className={filter === f.key ? 'chip active' : 'chip'} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label} <span className="chip-count">{all.filter((l) => matchesFilter(l, f.key, now)).length}</span>
            </button>
          ))}
        </div>
      </form>
      {confirmation && (
        <p className="alert alert-success" role="status">
          {confirmation}
        </p>
      )}
      {shown.length === 0 ? (
        <EmptyState title={all.length ? 'Keine Treffer' : 'Noch keine Kontakte'} icon={all.length ? 'help' : 'user'}>
          {all.length
            ? 'Für diese Auswahl gibt es keine Leads – Suche oder Auswahl ändern.'
            : 'Sobald Sie einen Termin, Rückruf oder eine Wiedervorlage haben, erscheint der Lead hier.'}
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table contacts-table">
            <thead>
              <tr>
                <th scope="col">Lead und Bezug</th>
                <th scope="col">Nachfassen</th>
                <th scope="col">Telefon</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((l) => (
                <Fragment key={l.lead_id}>
                  <tr className={needsFollowUp(l, now) ? 'row-attention' : undefined}>
                    <td>
                      <Link to={`/p/${project.key}/leads/${l.lead_id}`}>{l.name}</Link>
                      {(l.postal_code || l.city) && <small className="muted cell-sub">{[l.postal_code, l.city].filter(Boolean).join(' ')}</small>}
                      <span className="badges cell-badges">
                        {contactBadges(l, now).map((b) => (
                          <span key={b.kind} className={`badge badge-${b.tone}`}>
                            {b.label}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td>
                      <FollowUpState lead={l} now={now} attention={needsFollowUp(l, now)} formOpen={formFor === l.lead_id} onRequest={() => openForm(l.lead_id)} />
                    </td>
                    <td>{l.phone ? <a href={`tel:${l.phone}`}>{l.phone}</a> : '–'}</td>
                    <td>
                      <span className="badge">{labels[l.status] ?? l.status}</span>
                    </td>
                  </tr>
                  {formFor === l.lead_id && (
                    <tr className="request-row">
                      <td colSpan={4}>
                        <RequestForm
                          lead={l}
                          pending={request.isPending}
                          error={request.error}
                          onSubmit={(body) => request.mutate(body)}
                          onCancel={() => openForm(null)}
                          leadLink={`/p/${project.key}/leads/${l.lead_id}#rueckfragen`}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {query.data.truncated && <p className="muted">Es werden die ersten 1.000 Kontakte angezeigt.</p>}
    </div>
  );
}

// FollowUpState: wer dran ist (Support oder Makler selbst), das letzte Ergebnis und – wenn nichts läuft – „Nachfassen beauftragen“.
function FollowUpState({
  lead: l,
  now,
  attention,
  formOpen,
  onRequest,
}: {
  lead: BrokerLead;
  now: Date;
  attention: boolean;
  formOpen: boolean;
  onRequest: () => void;
}) {
  const t = l.team_follow_up;
  if (t) {
    // Überfällig: Der Support ist spät dran – sichtbar, damit der Makler nachhaken kann.
    const late = new Date(t.due_at) < now;
    return (
      <div className="followup-state">
        <span className={late ? 'badge badge-warn' : 'badge badge-info'}>{late ? 'Überfällig' : 'Läuft'}</span> {t.assignee_name ?? 'Terminplanung'} ·{' '}
        {fmtDayTime(t.due_at)}
        <small className="muted cell-sub">{t.requested_by_me ? 'Von Ihnen beauftragt' : t.type === 'CALLBACK' ? 'Rückruf durch die Terminplanung geplant' : 'Wiedervorlage bei der Terminplanung'}</small>
      </div>
    );
  }
  if (l.open_follow_up)
    return (
      <div className="followup-state">
        <span className="badge badge-muted">Bei Ihnen</span> {l.open_follow_up.type === 'CALLBACK' ? 'Rückruf' : 'Wiedervorlage'} {fmtDayTime(l.open_follow_up.due_at)}
      </div>
    );
  const r = l.last_result;
  return (
    <div className="followup-state">
      {r && (
        <small className="cell-sub">
          Ergebnis {fmtDay(r.completed_at)}: {r.result ?? (r.status === 'CANCELLED' ? 'beendet' : 'erledigt')}
          {r.by_name && <span className="muted"> ({r.by_name})</span>}
        </small>
      )}
      {canRequest(l) && !formOpen && (
        <button type="button" className={attention ? 'btn btn-small btn-primary' : 'btn btn-small'} onClick={onRequest}>
          Nachfassen beauftragen
        </button>
      )}
      {!r && !canRequest(l) && <span className="muted">–</span>}
    </div>
  );
}

function RequestForm({
  lead,
  pending,
  error,
  onSubmit,
  onCancel,
  leadLink,
}: {
  lead: BrokerLead;
  pending: boolean;
  error: unknown;
  onSubmit: (body: FollowUpRequest) => void;
  onCancel: () => void;
  leadLink: string;
}) {
  const [topic, setTopic] = useState<RequestTopic>(() => defaultTopic(lead));
  const [preset, setPreset] = useState<RequestPreset>('TOMORROW');
  const [note, setNote] = useState('');
  return (
    <form
      className="inline-panel request-form"
      aria-label={`Nachfassen beauftragen: ${lead.name}`}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ lead_id: lead.lead_id, topic, preset, note: note.trim() || undefined });
      }}
    >
      <p className="muted">Der Support fasst bei {lead.name} nach – in der Regel die Person, die den Termin vereinbart hat. Das Ergebnis bekommen Sie als Benachrichtigung.</p>
      <div className="inline-form">
        <label className="field">
          <span>Anlass</span>
          <select value={topic} onChange={(e) => setTopic(e.target.value as RequestTopic)}>
            {requestTopics.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field narrow">
          <span>Wann</span>
          <select value={preset} onChange={(e) => setPreset(e.target.value as RequestPreset)}>
            {requestPresets.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field wide">
        <span>Hinweis an die Terminplanung {topic === 'OTHER' ? '(Pflicht)' : '(optional)'}</span>
        <textarea rows={2} maxLength={600} value={note} required={topic === 'OTHER'} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error != null && <MutationError error={error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={pending}>
          Nachfassen beauftragen
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
        <Link className="request-form-link" to={leadLink}>
          Lieber eine Rückfrage stellen →
        </Link>
      </div>
    </form>
  );
}
